import {
  ForemanEmployeeDispatch,
  ForemanEmployeeLabor,
  ForemanJobDetail,
} from '@ups/xplat/api/dto';
import {
  captureForemanLaborCommittedState,
  getForemanLaborValuesSignature,
  hasForemanLaborDraftEdits,
} from './foreman-labor-draft.utils';

export interface RemoteLaborDraftReplacement {
  foremanEmployeeJobID: string;
  foremanEmployeeLaborID: string;
  /** Date/shift before overlay so a shift move still alerts on the viewed day. */
  previousDateWorked?: Date | string | null;
  previousShift?: number | null;
  dateWorked?: Date | string | null;
  shift?: number | null;
}

/**
 * The offline job cache merges remote data with cached priority
 * (`mergeObjectsCachedPriorityMut`), so once a labor row's `hours` / `bActive`
 * are cached, a refreshed value from the server is discarded. After an office
 * correction (Time Manager / web ITE), re-opening Individual Time Entry on the
 * device resurrects the stale cached hours while the DB is already correct
 * (ticket #30217).
 *
 * Clean cache rows always take the remote committed value. Dirty drafts whose
 * `committedSignature` still matches the remote values have not seen a new
 * server write, so the in-progress local edit is kept. When remote values
 * differ from that baseline, the server changed after the draft started (a
 * second web save, Time Manager correction) and remote wins even if the device
 * clock stamped the local edit later than `ModifiedDate`. A list/persist labor
 * that is itself still a draft is not a server write: overlaying it recaptured
 * unsaved 4h as committed and the next Shift refetch applied the saved 5h
 * (#30753). Rows with no fingerprint still compare
 * timezone-aware `modifiedDate` with `localChangedAtUtcMs`. A remote timestamp
 * without `Z` or an explicit offset is rejected because treating a clock face as
 * an instant would produce different winners in different timezones. A dirty
 * row whose values were edited back to the committed ones is treated as clean,
 * so a discarded edit cannot pin stale hours in place.
 *
 * Rows with no matching remote labor id are left untouched — the list API omits
 * inactive labors (`bActive == true` filter), and unconditional deactivate-on-
 * omit would also wipe cached labors older than `DaysBackToLoadForemanData`.
 */
export function reconcileLaborsFromRemoteJob(
  mergedJob: ForemanJobDetail,
  remoteJob: ForemanJobDetail
): RemoteLaborDraftReplacement[] {
  const replacements: RemoteLaborDraftReplacement[] = [];
  const remoteEmpById = new Map(
    (remoteJob.employees || []).map(
      (e) => [String(e.foremanEmployeeJobID || ''), e] as const
    )
  );

  for (const emp of mergedJob.employees || []) {
    const empId = String(emp.foremanEmployeeJobID || '');
    const remoteEmp: ForemanEmployeeDispatch | undefined = empId
      ? remoteEmpById.get(empId)
      : undefined;
    if (!remoteEmp) {
      continue;
    }

    const remoteLaborById = new Map(
      (remoteEmp.labors || []).map(
        (l) => [String(l.foremanEmployeeLaborID || ''), l] as const
      )
    );

    for (const labor of emp.labors || []) {
      const laborId = String(labor.foremanEmployeeLaborID || '');
      const remoteLabor: ForemanEmployeeLabor | undefined = laborId
        ? remoteLaborById.get(laborId)
        : undefined;
      if (!remoteLabor) {
        continue;
      }

      // A row edited back to its committed values is not a draft, so it never
      // wins the comparison and its replacement is not worth reporting.
      const hasDraftEdits = hasForemanLaborDraftEdits(labor);

      if (hasDraftEdits && !shouldOverlayRemoteOnDraft(labor, remoteLabor)) {
        continue;
      }

      const previousDateWorked = labor.dateWorked;
      const previousShift = labor.shift;
      const signatureBeforeOverlay = getForemanLaborValuesSignature(labor);
      overlayCommittedLaborFromRemote(labor, remoteLabor);
      captureForemanLaborCommittedState(labor);
      if (labor.isChanged === true) {
        labor.isChanged = false;
        delete labor.localChangedAtUtcMs;
      }
      // Only a draft the server actually contradicted is worth telling the user
      // about — a refresh right after submit returns the values just sent.
      if (
        hasDraftEdits &&
        labor.committedSignature !== signatureBeforeOverlay
      ) {
        replacements.push({
          foremanEmployeeJobID: empId,
          foremanEmployeeLaborID: laborId,
          previousDateWorked,
          previousShift,
          dateWorked: labor.dateWorked,
          shift: labor.shift,
        });
      }
    }
  }

  return replacements;
}

function shouldOverlayRemoteOnDraft(
  labor: ForemanEmployeeLabor,
  remoteLabor: ForemanEmployeeLabor
): boolean {
  // Persist and the job-list cache share ApplicationSettings. After a local
  // edit the "remote" labor on a shift-change refetch is often that same
  // draft (4h) not the server row (5h). Overlaying it recaptures 4h as
  // committed, clears amber, and the next fetch then applies the real 5h.
  if (
    hasForemanLaborDraftEdits(remoteLabor) ||
    remoteLabor.isChanged === true
  ) {
    return false;
  }

  if (typeof labor.committedSignature === 'string') {
    // The fingerprint is the last server state this draft started from. When
    // remote values still match it, a list refresh must not wipe in-progress
    // typing. When they differ, the server has a write the device has not
    // accepted, so remote wins regardless of Date.now() vs ModifiedDate.
    return (
      labor.committedSignature !== getForemanLaborValuesSignature(remoteLabor)
    );
  }

  return remoteIsStrictlyNewerThanLocalDraft(labor, remoteLabor);
}

function remoteIsStrictlyNewerThanLocalDraft(
  labor: ForemanEmployeeLabor,
  remoteLabor: ForemanEmployeeLabor
): boolean {
  const remoteUtcMs = parseTimezoneAwareUtcMs(remoteLabor.modifiedDate);
  if (remoteUtcMs === null) {
    return false;
  }

  const localUtcMs = labor.localChangedAtUtcMs;
  if (typeof localUtcMs !== 'number' || !Number.isFinite(localUtcMs)) {
    // Drafts cached before #30753 have no edit instant. A valid committed
    // server timestamp unblocks those legacy rows instead of preserving them
    // forever.
    return true;
  }

  // Strict `>`: ties keep the unsynced local draft. Used only when the row
  // has no committed baseline to compare.
  return remoteUtcMs > localUtcMs;
}

function parseTimezoneAwareUtcMs(
  value: Date | string | null | undefined
): number | null {
  if (value instanceof Date) {
    const dateUtcMs = value.getTime();
    return Number.isFinite(dateUtcMs) ? dateUtcMs : null;
  }
  if (typeof value !== 'string' || !/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) {
    return null;
  }

  const utcMs = Date.parse(value);
  return Number.isFinite(utcMs) ? utcMs : null;
}

function overlayCommittedLaborFromRemote(
  labor: ForemanEmployeeLabor,
  remoteLabor: ForemanEmployeeLabor
): void {
  labor.hours = remoteLabor.hours;
  labor.bActive = remoteLabor.bActive;
  labor.isAbsent = remoteLabor.isAbsent;
  labor.absentReasonID = remoteLabor.absentReasonID;
  labor.absentReason = remoteLabor.absentReason;
  labor.absentReasonOther = remoteLabor.absentReasonOther;
  labor.foremanNotes = remoteLabor.foremanNotes;
  labor.isException = remoteLabor.isException;
  labor.foremanExceptionNotes = remoteLabor.foremanExceptionNotes;
  labor.foremanPhaseID = remoteLabor.foremanPhaseID;
  labor.foremanEarnCode = remoteLabor.foremanEarnCode;
  labor.shift = remoteLabor.shift;
  labor.inOuts = remoteLabor.inOuts ?? [];
  labor.modifiedDate = remoteLabor.modifiedDate;
}
